import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Row,
  Col,
  Button,
  Spinner
} from 'react-bootstrap';
import { usersAPI } from '../services/api.js';
import { useTasks } from '../contexts/TasksContext';

const TaskFilters = ({ filters, onFilterChange, vertical = false }) => {
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const { getFullUserName } = useTasks();

  const [localSearch, setLocalSearch] = useState(filters.search || '');

  useEffect(() => {
    loadUsers();
  }, []);

  useEffect(() => {
    setLocalSearch(filters.search || '');
  }, [filters.search]);

  useEffect(() => {
    const handler = setTimeout(() => {
      if (localSearch !== filters.search) {
        handleFilterChange('search', localSearch);
      }
    }, 300);
    return () => clearTimeout(handler);
  }, [localSearch]);

  const loadUsers = async () => {
    try {
      setLoadingUsers(true);
      const response = await usersAPI.getUsers({ limit: 100 });
      const usersList = response.users || response.data || response;
      setUsers(Array.isArray(usersList) ? usersList : []);
    } catch (err) {
      console.error('Error loading users:', err);
      setUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleFilterChange = (key, value) => {
    const newFilters = {
      ...filters,
      [key]: value
    };
    onFilterChange(newFilters);
  };

  const clearFilters = () => {
    setLocalSearch('');
    onFilterChange({
      status: '',
      priority: '',
      category: '',
      assignee_id: '',
      search: ''
    });
  };

  const getFullName = (user) => {
    if (!user) return 'Не назначен';
    if (user.error) {
      return user.name || `Пользователь #${user.id}`;
    }
    const firstName = user.telegram_first_name || '';
    const lastName = user.telegram_last_name || '';
    const userName = user.name || '';
    if (firstName && lastName) {
      return `${firstName} ${lastName}`;
    } else if (firstName) {
      return firstName;
    } else if (lastName) {
      return lastName;
    } else if (userName) {
      return userName;
    }
    return 'Пользователь';
  };

  const statusOptions = [
    { value: '', label: 'Все статусы' },
    { value: 'open', label: 'Открыта' },
    { value: 'assigned', label: 'Назначена' },
    { value: 'in_progress', label: 'В работе' },
    { value: 'completed', label: 'Выполнена' },
    { value: 'cancelled', label: 'Отменена' },
    { value: 'reopened', label: 'Переоткрыта' }
  ];

  const priorityOptions = [
    { value: '', label: 'Все приоритеты' },
    { value: 'low', label: 'Низкий' },
    { value: 'medium', label: 'Средний' },
    { value: 'high', label: 'Высокий' },
    { value: 'urgent', label: 'Срочный' }
  ];

  const categoryOptions = [
    { value: '', label: 'Все категории' },
    { value: 'technical', label: 'Техническая' },
    { value: 'administrative', label: 'Административная' },
    { value: 'feature_request', label: 'Запрос функционала' },
    { value: 'bug', label: 'Ошибка' },
    { value: 'bil', label: 'БИЛ' },
    { value: 'invoices_registers', label: 'Счета Реестры' },
    { value: 'filling', label: 'Набивалка' },
    { value: 'other', label: 'Другое' }
  ];

  const hasActiveFilters = Object.values(filters).some(value => 
    value !== '' && value !== null && value !== undefined
  );

  if (vertical) {
    return (
      <div className="vertical-filters">
        <Form.Group className="mb-3">
          <Form.Label>Поиск</Form.Label>
          <Form.Control
            type="text"
            placeholder="Поиск по названию..."
            value={localSearch}
            onChange={(e) => setLocalSearch(e.target.value)}
          />
        </Form.Group>
        
        <Form.Group className="mb-3">
          <Form.Label>Статус</Form.Label>
          <Form.Select
            value={filters.status || ''}
            onChange={(e) => handleFilterChange('status', e.target.value)}
          >
            {statusOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
        
        <Form.Group className="mb-3">
          <Form.Label>Приоритет</Form.Label>
          <Form.Select
            value={filters.priority || ''}
            onChange={(e) => handleFilterChange('priority', e.target.value)}
          >
            {priorityOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
        
        <Form.Group className="mb-3">
          <Form.Label>Категория</Form.Label>
          <Form.Select
            value={filters.category || ''}
            onChange={(e) => handleFilterChange('category', e.target.value)}
          >
            {categoryOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
        
        <Form.Group className="mb-4">
          <Form.Label>Исполнитель</Form.Label>
          {loadingUsers ? (
            <div className="text-center py-2 border rounded bg-light">
              <Spinner animation="border" size="sm" />
              <span className="ms-2">Загрузка...</span>
            </div>
          ) : (
            <Form.Select
              value={filters.assignee_id || ''}
              onChange={(e) => handleFilterChange('assignee_id', e.target.value || '')}
            >
              <option value="">Все исполнители</option>
              {users.map(user => (
                <option key={user.id} value={user.id}>
                  {getFullName(user)} (ID: {user.id})
                  {user.telegram_username && ` - @${user.telegram_username}`}
                </option>
              ))}
            </Form.Select>
          )}
        </Form.Group>
        
        {hasActiveFilters && (
          <div className="mt-4 pt-3 border-top">
            <div className="d-flex flex-wrap gap-2 mb-3">
              {filters.search && (
                <span className="badge bg-primary">
                  Поиск: "{filters.search}"
                  <button 
                    className="btn-close btn-close-white ms-1" 
                    style={{ fontSize: '0.6rem' }}
                    onClick={() => {
                      setLocalSearch('');
                      handleFilterChange('search', '');
                    }}
                  ></button>
                </span>
              )}
              {filters.status && (
                <span className="badge bg-info">
                  Статус: {statusOptions.find(s => s.value === filters.status)?.label}
                  <button 
                    className="btn-close btn-close-white ms-1" 
                    style={{ fontSize: '0.6rem' }}
                    onClick={() => handleFilterChange('status', '')}
                  ></button>
                </span>
              )}
              {filters.priority && (
                <span className="badge bg-warning text-dark">
                  Приоритет: {priorityOptions.find(p => p.value === filters.priority)?.label}
                  <button 
                    className="btn-close ms-1" 
                    style={{ fontSize: '0.6rem' }}
                    onClick={() => handleFilterChange('priority', '')}
                  ></button>
                </span>
              )}
              {filters.category && (
                <span className="badge bg-secondary">
                  Категория: {categoryOptions.find(c => c.value === filters.category)?.label}
                  <button 
                    className="btn-close btn-close-white ms-1" 
                    style={{ fontSize: '0.6rem' }}
                    onClick={() => handleFilterChange('category', '')}
                  ></button>
                </span>
              )}
              {filters.assignee_id && (
                <span className="badge bg-success">
                  Исполнитель: {users.find(u => u.id.toString() === filters.assignee_id) ? 
                    getFullName(users.find(u => u.id.toString() === filters.assignee_id)) : 
                    `ID: ${filters.assignee_id}`}
                  <button 
                    className="btn-close btn-close-white ms-1" 
                    style={{ fontSize: '0.6rem' }}
                    onClick={() => handleFilterChange('assignee_id', '')}
                  ></button>
                </span>
              )}
            </div>
            <Button
              variant="outline-secondary"
              onClick={clearFilters}
              className="w-100"
            >
              <i className="bi bi-x-lg me-1"></i>
              Очистить все фильтры
            </Button>
          </div>
        )}
      </div>
    );
  }

  return (
    <Card className="mb-4">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <h6 className="mb-0">
          <i className="bi bi-funnel me-2"></i>
          Фильтры задач
        </h6>
        {hasActiveFilters && (
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={clearFilters}
          >
            <i className="bi bi-x-lg me-1"></i>
            Очистить фильтры
          </Button>
        )}
      </Card.Header>
      <Card.Body>
        <Row className="g-3">
          <Col md={3}>
            <Form.Group>
              <Form.Label>Поиск</Form.Label>
              <Form.Control
                type="text"
                placeholder="Поиск по названию..."
                value={localSearch}
                onChange={(e) => setLocalSearch(e.target.value)}
              />
            </Form.Group>
          </Col>
          
          <Col md={2}>
            <Form.Group>
              <Form.Label>Статус</Form.Label>
              <Form.Select
                value={filters.status || ''}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                {statusOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          
          <Col md={2}>
            <Form.Group>
              <Form.Label>Приоритет</Form.Label>
              <Form.Select
                value={filters.priority || ''}
                onChange={(e) => handleFilterChange('priority', e.target.value)}
              >
                {priorityOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          
          <Col md={2}>
            <Form.Group>
              <Form.Label>Категория</Form.Label>
              <Form.Select
                value={filters.category || ''}
                onChange={(e) => handleFilterChange('category', e.target.value)}
              >
                {categoryOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          
          <Col md={3}>
            <Form.Group>
              <Form.Label>Исполнитель</Form.Label>
              {loadingUsers ? (
                <div className="text-center py-2 border rounded bg-light">
                  <Spinner animation="border" size="sm" />
                  <span className="ms-2">Загрузка...</span>
                </div>
              ) : (
                <Form.Select
                  value={filters.assignee_id || ''}
                  onChange={(e) => handleFilterChange('assignee_id', e.target.value || '')}
                >
                  <option value="">Все исполнители</option>
                  {users.map(user => (
                    <option key={user.id} value={user.id}>
                      {getFullName(user)} (ID: {user.id})
                      {user.telegram_username && ` - @${user.telegram_username}`}
                    </option>
                  ))}
                </Form.Select>
              )}
            </Form.Group>
          </Col>
        </Row>
        
        {hasActiveFilters && (
          <Row className="mt-3">
            <Col>
              <div className="d-flex flex-wrap gap-2">
                {filters.search && (
                  <span className="badge bg-primary">
                    Поиск: "{filters.search}"
                    <button 
                      className="btn-close btn-close-white ms-1" 
                      style={{ fontSize: '0.6rem' }}
                      onClick={() => {
                        setLocalSearch('');
                        handleFilterChange('search', '');
                      }}
                    ></button>
                  </span>
                )}
                {filters.status && (
                  <span className="badge bg-info">
                    Статус: {statusOptions.find(s => s.value === filters.status)?.label}
                    <button 
                      className="btn-close btn-close-white ms-1" 
                      style={{ fontSize: '0.6rem' }}
                      onClick={() => handleFilterChange('status', '')}
                    ></button>
                  </span>
                )}
                {filters.priority && (
                  <span className="badge bg-warning text-dark">
                    Приоритет: {priorityOptions.find(p => p.value === filters.priority)?.label}
                    <button 
                      className="btn-close ms-1" 
                      style={{ fontSize: '0.6rem' }}
                      onClick={() => handleFilterChange('priority', '')}
                    ></button>
                  </span>
                )}
                {filters.category && (
                  <span className="badge bg-secondary">
                    Категория: {categoryOptions.find(c => c.value === filters.category)?.label}
                    <button 
                      className="btn-close btn-close-white ms-1" 
                      style={{ fontSize: '0.6rem' }}
                      onClick={() => handleFilterChange('category', '')}
                    ></button>
                  </span>
                )}
                {filters.assignee_id && (
                  <span className="badge bg-success">
                    Исполнитель: {users.find(u => u.id.toString() === filters.assignee_id) ? 
                      getFullName(users.find(u => u.id.toString() === filters.assignee_id)) : 
                      `ID: ${filters.assignee_id}`}
                    <button 
                      className="btn-close btn-close-white ms-1" 
                      style={{ fontSize: '0.6rem' }}
                      onClick={() => handleFilterChange('assignee_id', '')}
                    ></button>
                  </span>
                )}
              </div>
            </Col>
          </Row>
        )}
      </Card.Body>
    </Card>
  );
};

export default React.memo(TaskFilters);